import { useState, useEffect } from "react";
import API from "../lib/api";
import {
  Smartphone, Link2, Unlink, Users, Loader2, RefreshCw, Search, ChevronDown, Check
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function DeviceManagementPage() {
  const [devices, setDevices] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null); // { deviceId, deviceName }
  const [selectedChild, setSelectedChild] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [devRes, parRes] = await Promise.all([
        API.get("/admin/devices"),
        API.get("/admin/parents"),
      ]);
      setDevices(devRes.data.devices || []);
      setParents(parRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
  };

  const assignDevice = async () => {
    if (!selectedChild || !assignDialog) return;
    setAssigning(true);
    try {
      await API.post("/admin/devices/assign", {
        scalefusion_device_id: assignDialog.deviceId,
        child_id: selectedChild.child_id,
      });
      setAssignDialog(null);
      setSelectedChild(null);
      await fetchAll();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Failed to assign device");
    } finally {
      setAssigning(false);
    }
  };

  const unassignDevice = async (childId) => {
    try {
      await API.post("/admin/devices/unassign", { child_id: childId });
      await fetchAll();
    } catch (err) {
      console.error(err);
    }
  };

  // All children across all parents (for assignment)
  const allChildren = parents.flatMap((p) =>
    (p.children || []).map((c) => ({ ...c, parent_name: p.name, parent_email: p.email }))
  );

  const unassignedChildren = allChildren.filter((c) => !c.scalefusion_device_id);

  // Filter parents by search
  const filteredParents = parents.filter(
    (p) =>
      p.email?.toLowerCase().includes(search.toLowerCase()) ||
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.children?.some((c) => c.name?.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#FF5C39] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="device-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#1F1F2E] tracking-tight">
            Device Management
          </h1>
          <p className="text-sm font-semibold text-[#52526B] mt-1">
            Assign Scalefusion devices to parent accounts
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          data-testid="refresh-devices-button"
          className="bg-white border-2 border-[#F0E4D8] rounded-2xl p-3 hover:border-[#4D96FF] transition-all active:scale-95"
        >
          <RefreshCw className={`w-5 h-5 text-[#52526B] ${refreshing ? "animate-spin" : ""}`} strokeWidth={2.5} />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-[#F0E4D8]/50 text-center">
          <p className="text-2xl font-extrabold text-[#1F1F2E]">{devices.length}</p>
          <p className="text-xs font-bold text-[#52526B]">Scalefusion devices</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#F0E4D8]/50 text-center">
          <p className="text-2xl font-extrabold text-[#34D399]">
            {devices.filter((d) => d.is_assigned).length}
          </p>
          <p className="text-xs font-bold text-[#52526B]">Assigned</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#F0E4D8]/50 text-center">
          <p className="text-2xl font-extrabold text-[#FFD23F]">
            {devices.filter((d) => !d.is_assigned).length}
          </p>
          <p className="text-xs font-bold text-[#52526B]">Unassigned</p>
        </div>
      </div>

      {/* Scalefusion Devices Section */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#52526B] mb-3">
          Scalefusion Devices
        </h3>
        {devices.length === 0 ? (
          <div className="bg-white rounded-[32px] p-8 border border-[#F0E4D8]/50 text-center" data-testid="no-devices-card">
            <div className="w-16 h-16 bg-[#FFD23F]/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-[#FFD23F]" strokeWidth={2} />
            </div>
            <h3 className="text-lg font-extrabold text-[#1F1F2E] mb-2">No devices enrolled yet</h3>
            <p className="text-sm text-[#52526B] font-semibold max-w-md mx-auto">
              Enroll a Samsung A16 through the Scalefusion app by scanning the QR code on your
              Scalefusion dashboard. Once enrolled, it will appear here for you to assign to a parent.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {devices.map((device) => (
              <div
                key={device.id}
                data-testid={`device-row-${device.id}`}
                className="bg-white rounded-2xl p-4 border border-[#F0E4D8]/50 flex items-center gap-4"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  device.is_assigned ? "bg-[#34D399]/10" : "bg-[#FFD23F]/10"
                }`}>
                  <Smartphone className={`w-6 h-6 ${device.is_assigned ? "text-[#34D399]" : "text-[#FFD23F]"}`} strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#1F1F2E] truncate">{device.name || device.model || "Alfie Phone"}</p>
                  <p className="text-xs text-[#52526B] font-semibold">
                    {device.model} {device.serial_number ? `| SN: ${device.serial_number}` : ""}
                  </p>
                  {device.is_assigned && device.assignment && (
                    <p className="text-xs text-[#4D96FF] font-bold mt-0.5">
                      Assigned to {device.assignment.child_name} ({device.assignment.parent_email})
                    </p>
                  )}
                </div>
                <div>
                  {device.is_assigned ? (
                    <button
                      onClick={() => unassignDevice(device.assignment.child_id)}
                      data-testid={`unassign-device-${device.id}`}
                      className="bg-white text-[#F43F5E] font-bold rounded-xl px-3 py-2 text-xs border-2 border-[#F43F5E]/20 hover:bg-[#F43F5E]/5 active:scale-95 transition-all flex items-center gap-1"
                    >
                      <Unlink className="w-3.5 h-3.5" strokeWidth={2.5} />
                      Unassign
                    </button>
                  ) : (
                    <button
                      onClick={() => setAssignDialog({ deviceId: device.id, deviceName: device.name || device.model })}
                      data-testid={`assign-device-${device.id}`}
                      className="bg-[#FF5C39] text-white font-bold rounded-xl px-3 py-2 text-xs hover:bg-[#E04828] active:scale-95 transition-all flex items-center gap-1"
                    >
                      <Link2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                      Assign
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Parent Accounts Section */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#52526B] mb-3">
          Parent Accounts ({parents.length})
        </h3>
        <div className="mb-3 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52526B]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="parent-search-input"
            className="w-full bg-white border-2 border-[#F0E4D8] rounded-2xl pl-11 pr-4 py-3 text-sm font-semibold outline-none focus:border-[#4D96FF] transition-all placeholder:text-[#52526B]/50"
            placeholder="Search parents or children..."
          />
        </div>
        <div className="space-y-2">
          {filteredParents.map((parent) => (
            <div
              key={parent.id}
              data-testid={`parent-row-${parent.email}`}
              className="bg-white rounded-2xl p-4 border border-[#F0E4D8]/50"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#4D96FF] rounded-full flex items-center justify-center text-white font-extrabold text-sm">
                  {parent.name?.charAt(0)?.toUpperCase() || "P"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#1F1F2E] truncate">{parent.name}</p>
                  <p className="text-xs text-[#52526B] font-semibold truncate">{parent.email}</p>
                </div>
                <span className="text-xs font-bold text-[#52526B] bg-[#F0E4D8]/50 px-2 py-1 rounded-full">
                  {parent.children?.length || 0} kid{(parent.children?.length || 0) !== 1 ? "s" : ""}
                </span>
              </div>
              {parent.children?.map((child) => (
                <div key={child.child_id} className="ml-13 flex items-center gap-3 py-2 pl-3 border-l-2 border-[#F0E4D8]">
                  <div className="w-8 h-8 bg-[#FFD23F] rounded-full flex items-center justify-center text-[#1F1F2E] font-bold text-xs">
                    {child.name?.charAt(0)?.toUpperCase() || "C"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-xs text-[#1F1F2E]">{child.name} (age {child.age})</p>
                    {child.scalefusion_device_id ? (
                      <p className="text-xs text-[#34D399] font-bold">Device #{child.scalefusion_device_id} linked</p>
                    ) : (
                      <p className="text-xs text-[#FFD23F] font-bold">No device assigned</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Assign Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={(open) => !open && setAssignDialog(null)}>
        <DialogContent className="bg-white rounded-[32px] border-[#F0E4D8]">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-[#1F1F2E]">
              Assign {assignDialog?.deviceName || "device"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-[#52526B] font-semibold">
              Select a child to assign this device to:
            </p>
            {unassignedChildren.length === 0 ? (
              <p className="text-sm text-[#F43F5E] font-bold text-center py-4">
                All children already have devices assigned.
              </p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {unassignedChildren.map((child) => (
                  <button
                    key={child.child_id}
                    onClick={() => setSelectedChild(child)}
                    data-testid={`select-child-${child.child_id}`}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                      selectedChild?.child_id === child.child_id
                        ? "border-[#FF5C39] bg-[#FF5C39]/5"
                        : "border-[#F0E4D8] hover:border-[#4D96FF]"
                    }`}
                  >
                    <div className="w-8 h-8 bg-[#FFD23F] rounded-full flex items-center justify-center text-[#1F1F2E] font-bold text-xs">
                      {child.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-sm text-[#1F1F2E]">{child.name}</p>
                      <p className="text-xs text-[#52526B] font-semibold">{child.parent_name} ({child.parent_email})</p>
                    </div>
                    {selectedChild?.child_id === child.child_id && (
                      <Check className="w-5 h-5 text-[#FF5C39]" strokeWidth={3} />
                    )}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={assignDevice}
              disabled={!selectedChild || assigning}
              data-testid="confirm-assign-button"
              className="w-full bg-[#FF5C39] text-white font-bold rounded-2xl px-6 py-3.5 hover:bg-[#E04828] active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" strokeWidth={2.5} />}
              Assign device
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
